<script setup lang="ts">
import { useEventListener, useMediaQuery } from "@vueuse/core";
import { computed, nextTick, ref, useId, watch } from "vue";
import { useI18n } from "vue-i18n";
import VIcon from "../Icon/VIcon.vue";

const {
  title = "",
  ariaLabel = undefined,
  showBack = false,
  offsetBottom = "0px",
  zIndex = 60,
  maxHeight = "85dvh",
  tone = "muted",
} = defineProps<{
  /** Shown centered in the header and used as the dialog's accessible name. */
  title?: string
  /** Accessible name when there is no visible title. */
  ariaLabel?: string
  /** Shows a back button in the header that emits `back`, for sheets with nested views. */
  showBack?: boolean
  /** Distance from the bottom of the screen, e.g. to sit above a tab bar. */
  offsetBottom?: string
  zIndex?: number
  maxHeight?: string
  /** `muted` for menus (grey panel), `plain` for pickers (white panel with 16px corners). */
  tone?: "muted" | "plain"
}>();

const emit = defineEmits<{
  back: []
}>();

defineSlots<{
  default?: () => unknown
  /** Replaces the title row under the drag handle. */
  header?: () => unknown
}>();

/**
 * A panel that slides up from the bottom of the screen, for phone menus and pickers.
 * It is a modal dialog for assistive tech, closes on Escape, the backdrop or a drag down on its header,
 * moves focus in while open and back to where it was when it closes.
 */
const open = defineModel<boolean>("open", { default: false });

const { t, te } = useI18n();
const backLabel = computed(() => te("base.back") ? t("base.back") : "Back");
const closeLabel = computed(() => te("base.close") ? t("base.close") : "Close");

const titleId = useId();
const panel = ref<HTMLElement | null>(null);
const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
const duration = computed(() => reduceMotion.value ? 0 : 240);
const EASING = "cubic-bezier(0.32, 0.72, 0, 1)";

function close() {
  open.value = false;
}

// Dragging: follows the pointer down from the header; a long or fast enough pull closes the sheet.
const dragY = ref(0);
const isDragging = ref(false);
let pointerId: number | null = null;
let startY = 0;
let lastY = 0;
let lastTime = 0;
let velocity = 0;

function onPointerDown(event: PointerEvent) {
  if (event.pointerType === "mouse" && event.button !== 0) {
    return;
  }
  pointerId = event.pointerId;
  startY = event.clientY;
  lastY = event.clientY;
  lastTime = performance.now();
  velocity = 0;
}

useEventListener("pointermove", (event: PointerEvent) => {
  if (event.pointerId !== pointerId) {
    return;
  }
  const delta = event.clientY - startY;
  if (!isDragging.value && delta < 4) {
    return;
  }
  isDragging.value = true;
  const now = performance.now();
  velocity = (event.clientY - lastY) / Math.max(now - lastTime, 1);
  lastY = event.clientY;
  lastTime = now;
  dragY.value = Math.max(0, delta);
});

function endDrag(event: PointerEvent) {
  if (event.pointerId !== pointerId) {
    return;
  }
  pointerId = null;
  if (!isDragging.value) {
    return;
  }
  isDragging.value = false;
  const height = panel.value?.offsetHeight ?? 1;
  // A flick needs some travel too, so a tap with a twitch does not close the sheet.
  if (dragY.value > height * 0.3 || (velocity > 0.5 && dragY.value > 24)) {
    close();
    return;
  }
  const from = dragY.value;
  dragY.value = 0;
  panel.value?.animate(
    [{ transform: `translateY(${from}px)` }, { transform: "translateY(0)" }],
    { duration: duration.value, easing: EASING },
  );
}

useEventListener("pointerup", endDrag);
useEventListener("pointercancel", endDrag);

const panelStyle = computed(() => ({
  bottom: offsetBottom,
  zIndex,
  maxHeight: `calc(${maxHeight} - ${offsetBottom})`,
  transform: dragY.value ? `translateY(${dragY.value}px)` : undefined,
}));

const backdropOpacity = computed(() => {
  const height = panel.value?.offsetHeight ?? 1;
  return Math.max(0, 1 - dragY.value / height);
});

function onEnter(el: Element, done: () => void) {
  const animation = (el as HTMLElement).animate(
    [{ transform: "translateY(100%)" }, { transform: "translateY(0)" }],
    { duration: duration.value, easing: EASING },
  );
  animation.onfinish = done;
}

function onLeave(el: Element, done: () => void) {
  const from = dragY.value;
  const animation = (el as HTMLElement).animate(
    [{ transform: `translateY(${from}px)` }, { transform: "translateY(100%)" }],
    { duration: duration.value, easing: EASING, fill: "forwards" },
  );
  animation.onfinish = () => {
    dragY.value = 0;
    done();
  };
}

// Focus: into the sheet when it opens, back to the trigger when it closes, and Tab stays inside.
let returnFocusTo: HTMLElement | null = null;
watch(open, async (isOpen) => {
  if (isOpen) {
    returnFocusTo = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    await nextTick();
    panel.value?.focus({ preventScroll: true });
    return;
  }
  returnFocusTo?.focus({ preventScroll: true });
  returnFocusTo = null;
});

const FOCUSABLE = "a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])";

// Capture phase, so Escape closes only the sheet and not a dialog the sheet was opened from.
useEventListener(window, "keydown", (event: KeyboardEvent) => {
  if (!open.value) {
    return;
  }
  if (event.key === "Escape") {
    event.stopPropagation();
    close();
    return;
  }
  if (event.key !== "Tab" || !panel.value) {
    return;
  }
  const focusable = [...panel.value.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(el => el.offsetParent !== null);
  const first = focusable[0];
  const last = focusable.at(-1);
  if (!first || !last) {
    event.preventDefault();
    return;
  }
  if (event.shiftKey && (document.activeElement === first || document.activeElement === panel.value)) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}, { capture: true });
</script>

<template>
  <Transition
    enter-active-class="transition-opacity duration-200 motion-reduce:transition-none"
    leave-active-class="transition-opacity duration-200 motion-reduce:transition-none"
    enter-from-class="opacity-0"
    leave-to-class="opacity-0"
  >
    <div
      v-if="open"
      class="fixed inset-0 bg-black/40"
      :style="{ zIndex: zIndex - 1, opacity: isDragging ? backdropOpacity : undefined }"
      aria-hidden="true"
      @click="close"
    />
  </Transition>

  <Transition :css="false" @enter="onEnter" @leave="onLeave">
    <div
      v-if="open"
      ref="panel"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="title ? titleId : undefined"
      :aria-label="title ? undefined : ariaLabel"
      tabindex="-1"
      class="fixed inset-x-0 flex flex-col text-gray-800 dark:text-gray-100 outline-hidden"
      :class="tone === 'plain'
        ? 'rounded-t-2xl bg-white dark:bg-dark-800 shadow-[0_-6px_24px_rgba(0,0,0,0.12)]'
        : 'rounded-t-3xl bg-gray-50 dark:bg-dark-900 shadow-[0_-8px_30px_rgba(0,0,0,0.12)]'"
      :style="panelStyle"
    >
      <div
        class="shrink-0 pt-2 pb-3 touch-none cursor-grab select-none"
        :class="{ 'cursor-grabbing': isDragging }"
        @pointerdown="onPointerDown"
      >
        <div class="mx-auto h-1 w-10 rounded-full bg-gray-300 dark:bg-dark-600" aria-hidden="true" />
        <slot name="header">
          <div v-if="title || showBack" class="relative flex items-center justify-center h-10 mt-2 px-4">
            <button
              v-if="showBack"
              type="button"
              class="absolute left-4 flex items-center justify-center w-9 h-9 rounded-full bg-white dark:bg-dark-800 text-gray-600 dark:text-gray-300 shadow-[0_1px_2px_rgba(0,0,0,0.08)] active:scale-95 transition-transform"
              :aria-label="backLabel"
              @pointerdown.stop
              @click="emit('back')"
            >
              <VIcon name="chevron-left" class="text-sm" />
            </button>
            <h2 :id="titleId" class="max-w-[60%] truncate text-base font-semibold">
              {{ title }}
            </h2>
          </div>
        </slot>
        <button type="button" class="sr-only" @click="close">
          {{ closeLabel }}
        </button>
      </div>

      <div class="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-5">
        <slot />
      </div>
    </div>
  </Transition>
</template>
